// TelcoTrack — weld-layout v2 parity fixtures (TS Jest + KMP JSON vectors).
// Cassette racks: MUK rows (KM4/KM5 family), PON32 4×KM6 with loose splitters.

import type {
  RozvadecWeldCassette,
  RozvadecWeldIncomingRef,
  RozvadecWeldLayout,
  RozvadecWeldLayoutBuilding,
  RozvadecWeldLayoutCable,
  RozvadecWeldLayoutContext,
  RozvadecWeldLayoutExportPayload,
  RozvadecWeldLayoutParityFixture,
  RozvadecWeldLink,
  RozvadecWeldTargetRef,
  RozvadecWeldTermination,
} from './rozvadec-weld-layout.types';
import {
  ROZVADEC_WELD_CASSETTE_POSITION_BASE,
  ROZVADEC_WELD_CASSETTE_SLOT_BASE,
  ROZVADEC_WELD_LAYOUT_ISSUE_CODES,
  ROZVADEC_WELD_LAYOUT_SPLITTER_OUTPUT_BASE,
  ROZVADEC_WELD_LAYOUT_VERSION,
  ROZVADEC_WELD_LINK_KINDS,
  ROZVADEC_WELD_TERMINATION_KINDS,
} from './rozvadec-weld-layout.types';
import type { RozvadecCassetteTypeId } from './rozvadec-cassette.constants';
import type { RozvadecSplitterRatio } from './rozvadec-cabinet.constants';
import { incomingKeysForCable } from './rozvadec-weld-layout.derive';

const FEEDER_12: RozvadecWeldLayoutCable = {
  cableId: 'feeder-a',
  typOk: '12 vl.',
  fiberCount: 12,
};

const BUILDING_A: RozvadecWeldLayoutBuilding = {
  buildingId: 'b1',
  cableId: 'out-1',
  typOk: '2 vl.',
  fiberCount: 2,
};

const BUILDING_B: RozvadecWeldLayoutBuilding = {
  buildingId: 'b2',
  cableId: 'out-2',
  typOk: '2 vl.',
  fiberCount: 2,
};

function incoming(physicalFiber: number, cableId = 'feeder-a'): RozvadecWeldIncomingRef {
  return { cableId, physicalFiber };
}

function target(
  building: RozvadecWeldLayoutBuilding,
  physicalFiber = 1,
): RozvadecWeldTargetRef {
  return {
    buildingId: building.buildingId,
    cableId: building.cableId as string,
    physicalFiber,
  };
}

function ctx(partial: RozvadecWeldLayoutContext): RozvadecWeldLayoutContext {
  return {
    typeId: partial.typeId ?? null,
    derivedFromSap: partial.derivedFromSap ?? null,
    materials: partial.materials ?? [],
    incomingCables: partial.incomingCables ?? [],
    outgoingCables: partial.outgoingCables ?? [],
    buildings: partial.buildings ?? [],
  };
}

function layout(
  offers: RozvadecWeldLayout['offers'],
  cassettes: RozvadecWeldCassette[],
  terminations: RozvadecWeldTermination[],
  links: RozvadecWeldLink[],
): RozvadecWeldLayout {
  return { version: 2, offers, cassettes, terminations, links };
}

function cassette(
  id: string,
  cassetteType: RozvadecCassetteTypeId,
  position: number,
  sectionId = 'rada_1',
): RozvadecWeldCassette {
  return { id, cassetteType, sectionId, position };
}

function connectorTerm(
  id: string,
  cassetteId: string,
  slotInCassette: number,
  physicalFiber: number,
): RozvadecWeldTermination {
  return {
    id,
    kind: 'connector',
    cassetteId,
    slotInCassette,
    incoming: incoming(physicalFiber),
    splitterRatio: null,
  };
}

function splitterTerm(
  id: string,
  cassetteId: string | null,
  slotInCassette: number | null,
  physicalFiber: number | null,
  splitterRatio: RozvadecSplitterRatio | null,
): RozvadecWeldTermination {
  return {
    id,
    kind: 'splitter',
    cassetteId,
    slotInCassette,
    incoming: physicalFiber == null ? null : incoming(physicalFiber),
    splitterRatio,
  };
}

function directLink(id: string, physicalFiber: number, building: RozvadecWeldLayoutBuilding): RozvadecWeldLink {
  return {
    id,
    kind: 'direct',
    incoming: incoming(physicalFiber),
    peerIncoming: null,
    cassetteId: null,
    terminationId: null,
    splitterOutput: null,
    target: target(building),
  };
}

function cassettePairLink(
  id: string,
  cassetteId: string,
  fiberA: number,
  fiberB: number | null,
): RozvadecWeldLink {
  return {
    id,
    kind: 'direct',
    incoming: incoming(fiberA),
    peerIncoming: fiberB == null ? null : incoming(fiberB),
    cassetteId,
    terminationId: null,
    splitterOutput: null,
    target: null,
  };
}

function connectorLink(id: string, terminationId: string, building: RozvadecWeldLayoutBuilding): RozvadecWeldLink {
  return {
    id,
    kind: 'connector',
    incoming: null,
    peerIncoming: null,
    cassetteId: null,
    terminationId,
    splitterOutput: null,
    target: target(building),
  };
}

function splitterLink(
  id: string,
  terminationId: string,
  splitterOutput: number,
  building: RozvadecWeldLayoutBuilding,
): RozvadecWeldLink {
  return {
    id,
    kind: 'splitter',
    incoming: null,
    peerIncoming: null,
    cassetteId: null,
    terminationId,
    splitterOutput,
    target: target(building),
  };
}

const MUK1_CONTEXT = ctx({
  typeId: 'MUK_1X24',
  incomingCables: [FEEDER_12],
  buildings: [BUILDING_A, BUILDING_B],
});

const MUK2_CONTEXT = ctx({
  typeId: 'MUK_2X24',
  incomingCables: [FEEDER_12],
  buildings: [BUILDING_A, BUILDING_B],
});

const PON32_CONTEXT = ctx({
  typeId: 'PON32',
  incomingCables: [FEEDER_12],
  buildings: [BUILDING_A, BUILDING_B],
});

const OS_CONTEXT = ctx({
  typeId: 'OS',
  materials: [{ sapCode: '325739', quantity: 1 }],
  incomingCables: [FEEDER_12],
  buildings: [BUILDING_A, BUILDING_B],
});

const OS_UNKNOWN_CONTEXT = ctx({
  typeId: 'OS',
  materials: [{ sapCode: '319647', quantity: 1 }],
  incomingCables: [FEEDER_12],
  buildings: [BUILDING_A],
});

const COMBINED = { directEnabled: true, connectorsEnabled: true };
const DIRECT_ONLY = { directEnabled: true, connectorsEnabled: false };

const ALL_FREE = incomingKeysForCable(FEEDER_12);

function freeWithout(...fibers: number[]): string[] {
  const used = new Set(fibers.map((fiber) => `feeder-a#${fiber}`));
  return ALL_FREE.filter((key) => !used.has(key));
}

export const ROZVADEC_WELD_LAYOUT_PARITY_FIXTURES: readonly RozvadecWeldLayoutParityFixture[] = [
  {
    id: 'muk-combined-offers-empty',
    title: 'MUK combinable offers with no cassettes — primary complete, secondary empty',
    layout: layout(COMBINED, [], [], []),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: [],
      counts: { weldCountPrimary: 0, weldCountSecondary: 0, cassetteCount: 0, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: [],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: true,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'os-direct-only-reject-connectors',
    title: 'OS rejects connectorsEnabled',
    layout: layout(COMBINED, [], [], []),
    context: OS_CONTEXT,
    expect: {
      issueCodes: ['OFFERS_OS_DIRECT_ONLY'],
      counts: { weldCountPrimary: 0, weldCountSecondary: 0, cassetteCount: 0, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: [],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'os-direct-only-valid-empty',
    title: 'OS direct-only primary is complete with zero terminations',
    layout: layout(DIRECT_ONLY, [], [], []),
    context: OS_CONTEXT,
    expect: {
      issueCodes: [],
      counts: { weldCountPrimary: 0, weldCountSecondary: 0, cassetteCount: 0, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: [],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: true,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk-km4-direct-weld',
    title: 'Direct weld executed in an installed KM4 tray',
    layout: layout(COMBINED, [cassette('cas-km4', 'KM4', 0)], [], [directLink('d-1', 1, BUILDING_A)]),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: [],
      counts: { weldCountPrimary: 0, weldCountSecondary: 1, cassetteCount: 1, cassetteWeldCapacity: 24 },
      occupiedRackPositionKeys: ['rada_1#0'],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: true,
      completeSecondary: true,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk-direct-without-tray',
    title: 'Direct weld without any KM4/KM6 tray exceeds cassette weld capacity',
    layout: layout(COMBINED, [], [], [directLink('d-1', 1, BUILDING_A)]),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['CASSETTE_WELD_CAPACITY_EXCEEDED'],
      counts: { weldCountPrimary: 0, weldCountSecondary: 1, cassetteCount: 0, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: [],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk-km5-connector',
    title: 'KM5 spans two positions; connector adapter slot pairs feeder to building',
    layout: layout(
      COMBINED,
      [cassette('cas-km5', 'KM5_SC', 0)],
      [connectorTerm('t-1', 'cas-km5', 3, 1)],
      [connectorLink('c-1', 't-1', BUILDING_A)],
    ),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: [],
      counts: { weldCountPrimary: 1, weldCountSecondary: 1, cassetteCount: 1, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: ['rada_1#0', 'rada_1#1'],
      occupiedCassetteSlotKeys: ['cas-km5#3'],
      freeIncomingKeys: freeWithout(1),
      completePrimary: true,
      completeSecondary: true,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk-km5-slot-occupied',
    title: 'Two terminations cannot share one adapter slot',
    layout: layout(
      COMBINED,
      [cassette('cas-km5', 'KM5_SC', 0)],
      [connectorTerm('t-a', 'cas-km5', 1, 1), connectorTerm('t-b', 'cas-km5', 1, 2)],
      [],
    ),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['TERMINATION_SLOT_OCCUPIED'],
      counts: { weldCountPrimary: 2, weldCountSecondary: 0, cassetteCount: 1, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: ['rada_1#0', 'rada_1#1'],
      occupiedCassetteSlotKeys: ['cas-km5#1'],
      freeIncomingKeys: freeWithout(1, 2),
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk-cassette-overlap',
    title: 'KM5 span overlaps a KM4 anchored one position above',
    layout: layout(
      COMBINED,
      [cassette('cas-km5', 'KM5_SC', 0), cassette('cas-km4', 'KM4', 1)],
      [],
      [],
    ),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['CASSETTE_POSITION_OVERLAP'],
      counts: { weldCountPrimary: 0, weldCountSecondary: 0, cassetteCount: 2, cassetteWeldCapacity: 24 },
      occupiedRackPositionKeys: ['rada_1#0', 'rada_1#1'],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk-span-out-of-section',
    title: 'KM5 anchored at the last position does not fit',
    layout: layout(COMBINED, [cassette('cas-km5', 'KM5_SC', 23)], [], []),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['CASSETTE_SPAN_OUT_OF_SECTION'],
      counts: { weldCountPrimary: 0, weldCountSecondary: 0, cassetteCount: 1, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: ['rada_1#23', 'rada_1#24'],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk-position-out-of-range',
    title: 'Rack rejects a cassette position outside the section',
    layout: layout(COMBINED, [cassette('cas-km4', 'KM4', 24)], [], []),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['CASSETTE_POSITION_INVALID'],
      counts: { weldCountPrimary: 0, weldCountSecondary: 0, cassetteCount: 1, cassetteWeldCapacity: 24 },
      occupiedRackPositionKeys: ['rada_1#24'],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk-unknown-section',
    title: 'Rack rejects an unknown section id',
    layout: layout(COMBINED, [cassette('cas-km4', 'KM4', 0, 'rada_9')], [], []),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['CASSETTE_POSITION_INVALID'],
      counts: { weldCountPrimary: 0, weldCountSecondary: 0, cassetteCount: 1, cassetteWeldCapacity: 24 },
      occupiedRackPositionKeys: ['rada_9#0'],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk-connector-in-km4',
    title: 'KM4 is a weld tray — connector terminations are not allowed',
    layout: layout(
      COMBINED,
      [cassette('cas-km4', 'KM4', 0)],
      [connectorTerm('t-1', 'cas-km4', 1, 1)],
      [],
    ),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['TERMINATION_CONTENT_NOT_ALLOWED'],
      counts: { weldCountPrimary: 1, weldCountSecondary: 0, cassetteCount: 1, cassetteWeldCapacity: 24 },
      occupiedRackPositionKeys: ['rada_1#0'],
      occupiedCassetteSlotKeys: ['cas-km4#1'],
      freeIncomingKeys: freeWithout(1),
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk-splitter-in-plain-km5',
    title: 'Plain KM5 has no preinstalled splitters',
    layout: layout(
      COMBINED,
      [cassette('cas-km5', 'KM5_SC', 0)],
      [splitterTerm('spl-1', 'cas-km5', 1, 2, '1:8')],
      [],
    ),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['CASSETTE_SPLITTER_NOT_PREINSTALLED'],
      counts: { weldCountPrimary: 1, weldCountSecondary: 0, cassetteCount: 1, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: ['rada_1#0', 'rada_1#1'],
      occupiedCassetteSlotKeys: ['cas-km5#1'],
      freeIncomingKeys: freeWithout(2),
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk-preinstalled-splitter-ok',
    title: 'KM5 2×1:8 hosts an unpaired preinstalled splitter',
    layout: layout(
      COMBINED,
      [cassette('cas-s8', 'KM5_SPLITTER_2X8', 0)],
      [splitterTerm('spl-1', 'cas-s8', 2, null, '1:8')],
      [],
    ),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: [],
      counts: { weldCountPrimary: 1, weldCountSecondary: 0, cassetteCount: 1, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: ['rada_1#0', 'rada_1#1'],
      occupiedCassetteSlotKeys: ['cas-s8#2'],
      freeIncomingKeys: ALL_FREE,
      completePrimary: true,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk-preinstalled-ratio-mismatch',
    title: 'Preinstalled splitter ratio is fixed by the SAP variant',
    layout: layout(
      COMBINED,
      [cassette('cas-s8', 'KM5_SPLITTER_2X8', 0)],
      [splitterTerm('spl-1', 'cas-s8', 1, 2, '1:16')],
      [],
    ),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['CASSETTE_SPLITTER_NOT_PREINSTALLED'],
      counts: { weldCountPrimary: 1, weldCountSecondary: 0, cassetteCount: 1, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: ['rada_1#0', 'rada_1#1'],
      occupiedCassetteSlotKeys: ['cas-s8#1'],
      freeIncomingKeys: freeWithout(2),
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk-adapter-slot-out-of-range',
    title: 'KM5 has 12 adapter slots',
    layout: layout(
      COMBINED,
      [cassette('cas-km5', 'KM5_SC', 0)],
      [connectorTerm('t-1', 'cas-km5', 13, 1)],
      [],
    ),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['CASSETTE_ADAPTER_CAPACITY_EXCEEDED'],
      counts: { weldCountPrimary: 1, weldCountSecondary: 0, cassetteCount: 1, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: ['rada_1#0', 'rada_1#1'],
      occupiedCassetteSlotKeys: ['cas-km5#13'],
      freeIncomingKeys: freeWithout(1),
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'muk48-right-row-top-fill',
    title: 'MUK 2×24 right row is numbered from the top',
    layout: layout(COMBINED, [cassette('cas-km4', 'KM4', 0, 'rada_2')], [], []),
    context: MUK2_CONTEXT,
    expect: {
      issueCodes: [],
      counts: { weldCountPrimary: 0, weldCountSecondary: 0, cassetteCount: 1, cassetteWeldCapacity: 24 },
      occupiedRackPositionKeys: ['rada_2#0'],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: true,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'pon32-km6-loose-splitter',
    title: 'PON32: 4×KM6 trays, splitter outside cassettes, direct weld in tray',
    layout: layout(
      COMBINED,
      [
        cassette('cas-km6-1', 'KM6', 0, 'rack'),
        cassette('cas-km6-2', 'KM6', 1, 'rack'),
        cassette('cas-km6-3', 'KM6', 2, 'rack'),
        cassette('cas-km6-4', 'KM6', 3, 'rack'),
      ],
      [splitterTerm('spl-1', null, null, 2, '1:8')],
      [splitterLink('l-1', 'spl-1', 1, BUILDING_A), directLink('d-1', 3, BUILDING_B)],
    ),
    context: PON32_CONTEXT,
    expect: {
      issueCodes: [],
      counts: { weldCountPrimary: 1, weldCountSecondary: 2, cassetteCount: 4, cassetteWeldCapacity: 96 },
      occupiedRackPositionKeys: ['rack#0', 'rack#1', 'rack#2', 'rack#3'],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: freeWithout(2),
      completePrimary: true,
      completeSecondary: true,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'pon32-km6-incoming-pair',
    title: 'PON32: KM6 on bay 1 hosts an incoming↔incoming pair (primary napřímo)',
    layout: layout(
      COMBINED,
      [cassette('cas-km6-1', 'KM6', 0, 'rack')],
      [],
      [cassettePairLink('p-1', 'cas-km6-1', 1, 2)],
    ),
    context: PON32_CONTEXT,
    expect: {
      issueCodes: [],
      counts: { weldCountPrimary: 1, weldCountSecondary: 0, cassetteCount: 1, cassetteWeldCapacity: 24 },
      occupiedRackPositionKeys: ['rack#0'],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: true,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'pon32-km5-not-allowed',
    title: 'PON32 rack accepts only KM6',
    layout: layout(COMBINED, [cassette('cas-km5', 'KM5_SC', 0, 'rack')], [], []),
    context: PON32_CONTEXT,
    expect: {
      issueCodes: ['CASSETTE_TYPE_INVALID'],
      counts: { weldCountPrimary: 0, weldCountSecondary: 0, cassetteCount: 1, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: ['rack#0', 'rack#1'],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'pon32-connector-requires-cassette',
    title: 'Connectors on PON32 must live in a cassette',
    layout: layout(
      COMBINED,
      [],
      [
        {
          id: 't-loose',
          kind: 'connector',
          cassetteId: null,
          slotInCassette: null,
          incoming: incoming(1),
          splitterRatio: null,
        },
      ],
      [],
    ),
    context: PON32_CONTEXT,
    expect: {
      issueCodes: ['TERMINATION_CASSETTE_MISSING'],
      counts: { weldCountPrimary: 1, weldCountSecondary: 0, cassetteCount: 0, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: [],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: freeWithout(1),
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'termination-cassette-unknown',
    title: 'Termination cannot reference a cassette that does not exist',
    layout: layout(COMBINED, [], [connectorTerm('t-1', 'cas-ghost', 1, 1)], []),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['TERMINATION_CASSETTE_MISSING'],
      counts: { weldCountPrimary: 1, weldCountSecondary: 0, cassetteCount: 0, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: [],
      occupiedCassetteSlotKeys: ['cas-ghost#1'],
      freeIncomingKeys: freeWithout(1),
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'duplicate-source-direct',
    title: 'Direct links cannot share the same incoming pair',
    layout: layout(
      COMBINED,
      [cassette('cas-km4', 'KM4', 0)],
      [],
      [directLink('d-1', 4, BUILDING_A), directLink('d-2', 4, BUILDING_B)],
    ),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['LINK_SOURCE_OCCUPIED'],
      counts: { weldCountPrimary: 0, weldCountSecondary: 2, cassetteCount: 1, cassetteWeldCapacity: 24 },
      occupiedRackPositionKeys: ['rada_1#0'],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'duplicate-target',
    title: 'Two links cannot target the same building fiber',
    layout: layout(
      COMBINED,
      [cassette('cas-km5', 'KM5_SC', 0)],
      [connectorTerm('t-1', 'cas-km5', 1, 1), connectorTerm('t-2', 'cas-km5', 2, 3)],
      [connectorLink('c-1', 't-1', BUILDING_A), connectorLink('c-2', 't-2', BUILDING_A)],
    ),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['LINK_TARGET_OCCUPIED'],
      counts: { weldCountPrimary: 2, weldCountSecondary: 2, cassetteCount: 1, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: ['rada_1#0', 'rada_1#1'],
      occupiedCassetteSlotKeys: ['cas-km5#1', 'cas-km5#2'],
      freeIncomingKeys: freeWithout(1, 3),
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'direct-from-terminated-fiber',
    title: 'Direct source cannot be a terminated incoming pair',
    layout: layout(
      COMBINED,
      [cassette('cas-km4', 'KM4', 0), cassette('cas-km5', 'KM5_SC', 1)],
      [connectorTerm('t-1', 'cas-km5', 1, 1)],
      [directLink('d-1', 1, BUILDING_A)],
    ),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['LINK_DIRECT_SOURCE_TERMINATED'],
      counts: { weldCountPrimary: 1, weldCountSecondary: 1, cassetteCount: 2, cassetteWeldCapacity: 24 },
      occupiedRackPositionKeys: ['rada_1#0', 'rada_1#1', 'rada_1#2'],
      occupiedCassetteSlotKeys: ['cas-km5#1'],
      freeIncomingKeys: freeWithout(1),
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'splitter-output-out-of-range',
    title: 'Splitter output must be in 1..N',
    layout: layout(
      COMBINED,
      [],
      [splitterTerm('spl-1', null, null, 5, '1:8')],
      [splitterLink('l-bad', 'spl-1', 9, BUILDING_A)],
    ),
    context: PON32_CONTEXT,
    expect: {
      issueCodes: ['LINK_SPLITTER_OUTPUT_INVALID'],
      counts: { weldCountPrimary: 1, weldCountSecondary: 1, cassetteCount: 0, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: [],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: freeWithout(5),
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'offers-none',
    title: 'At least one offer must be enabled',
    layout: layout({ directEnabled: false, connectorsEnabled: false }, [], [], []),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['OFFERS_NONE_ENABLED'],
      counts: { weldCountPrimary: 0, weldCountSecondary: 0, cassetteCount: 0, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: [],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'connectors-disabled-with-termination',
    title: 'Terminations are forbidden when connectorsEnabled is false',
    layout: layout(
      DIRECT_ONLY,
      [cassette('cas-km5', 'KM5_SC', 0)],
      [connectorTerm('t-1', 'cas-km5', 1, 1)],
      [],
    ),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['OFFERS_CONNECTORS_DISABLED'],
      counts: { weldCountPrimary: 1, weldCountSecondary: 0, cassetteCount: 1, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: ['rada_1#0', 'rada_1#1'],
      occupiedCassetteSlotKeys: ['cas-km5#1'],
      freeIncomingKeys: freeWithout(1),
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'unit-label-rejected',
    title: 'New writes must not carry unitLabel',
    raw: {
      version: 2,
      offers: COMBINED,
      cassettes: [cassette('cas-km4', 'KM4', 0)],
      terminations: [],
      links: [
        {
          ...directLink('d-1', 6, BUILDING_A),
          unitLabel: '1',
        },
      ],
    },
    layout: layout(COMBINED, [cassette('cas-km4', 'KM4', 0)], [], [directLink('d-1', 6, BUILDING_A)]),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['LINK_UNIT_LABEL_FORBIDDEN'],
      counts: { weldCountPrimary: 0, weldCountSecondary: 1, cassetteCount: 1, cassetteWeldCapacity: 24 },
      occupiedRackPositionKeys: ['rada_1#0'],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'connector-link-without-termination',
    title: 'Connector link must reference an existing termination',
    layout: layout(COMBINED, [], [], [connectorLink('c-missing', 'missing', BUILDING_A)]),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['LINK_TERMINATION_MISSING'],
      counts: { weldCountPrimary: 0, weldCountSecondary: 1, cassetteCount: 0, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: [],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'splitter-ratio-missing',
    title: 'Splitter termination requires an approved ratio',
    layout: layout(COMBINED, [], [splitterTerm('spl-bare', null, null, 7, null)], []),
    context: PON32_CONTEXT,
    expect: {
      issueCodes: ['TERMINATION_RATIO_REQUIRED'],
      counts: { weldCountPrimary: 1, weldCountSecondary: 0, cassetteCount: 0, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: [],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: freeWithout(7),
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'os-unknown-capacity',
    title: 'Unknown OS SAP cap blocks secondary confirmation',
    layout: layout(DIRECT_ONLY, [], [], [directLink('d-1', 1, BUILDING_A)]),
    context: OS_UNKNOWN_CONTEXT,
    expect: {
      issueCodes: ['CAPACITY_OS_UNKNOWN'],
      counts: { weldCountPrimary: 0, weldCountSecondary: 1, cassetteCount: 0, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: [],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: true,
    },
  },
  {
    id: 'combined-direct-and-connector',
    title: 'Combinable modes: tray direct weld plus KM5 connector 1:1',
    layout: layout(
      COMBINED,
      [cassette('cas-km4', 'KM4', 0), cassette('cas-km5', 'KM5_SC', 1)],
      [connectorTerm('t-1', 'cas-km5', 1, 1)],
      [directLink('d-1', 2, BUILDING_A), connectorLink('c-1', 't-1', BUILDING_B)],
    ),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: [],
      counts: { weldCountPrimary: 1, weldCountSecondary: 2, cassetteCount: 2, cassetteWeldCapacity: 24 },
      occupiedRackPositionKeys: ['rada_1#0', 'rada_1#1', 'rada_1#2'],
      occupiedCassetteSlotKeys: ['cas-km5#1'],
      freeIncomingKeys: freeWithout(1),
      completePrimary: true,
      completeSecondary: true,
      unknownOsCapacity: false,
    },
  },
  {
    id: 'version-unsupported',
    title: 'Only contract version 2 is accepted',
    raw: { version: 1, offers: COMBINED, cassettes: [], terminations: [], links: [] },
    layout: layout(COMBINED, [], [], []),
    context: MUK1_CONTEXT,
    expect: {
      issueCodes: ['VERSION_UNSUPPORTED'],
      counts: { weldCountPrimary: 0, weldCountSecondary: 0, cassetteCount: 0, cassetteWeldCapacity: 0 },
      occupiedRackPositionKeys: [],
      occupiedCassetteSlotKeys: [],
      freeIncomingKeys: ALL_FREE,
      completePrimary: false,
      completeSecondary: false,
      unknownOsCapacity: false,
    },
  },
];

export function buildRozvadecWeldLayoutExportPayload(): RozvadecWeldLayoutExportPayload {
  return JSON.parse(
    JSON.stringify({
      contractVersion: ROZVADEC_WELD_LAYOUT_VERSION,
      cassettePositionBase: ROZVADEC_WELD_CASSETTE_POSITION_BASE,
      cassetteSlotBase: ROZVADEC_WELD_CASSETTE_SLOT_BASE,
      splitterOutputBase: ROZVADEC_WELD_LAYOUT_SPLITTER_OUTPUT_BASE,
      terminationKinds: [...ROZVADEC_WELD_TERMINATION_KINDS],
      linkKinds: [...ROZVADEC_WELD_LINK_KINDS],
      issueCodes: [...ROZVADEC_WELD_LAYOUT_ISSUE_CODES],
      offerRules: {
        combinable: true,
        osForcesDirectOnly: true,
        newDirectsOsOnly: true,
        primaryDirectCreatesNoTermination: true,
        cassettePairsArePrimaryDirects: true,
        noUnitLabel: true,
      },
      countSemantics: {
        primary: 'terminations + cassette pairs',
        secondary: 'building-targeted links',
      },
      fixtures: ROZVADEC_WELD_LAYOUT_PARITY_FIXTURES,
    }),
  ) as RozvadecWeldLayoutExportPayload;
}
